import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Wallet as WalletIcon } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useWallet } from '../hooks/useWallet'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { getStripePublishableKey, walletReturnUrl } from '../lib/stripe'
import { WalletBalanceCard } from '../components/wallet/WalletBalanceCard'
import { TopUpDialog } from '../components/wallet/TopUpDialog'
import { TopUpPaymentForm } from '../components/wallet/TopUpPaymentForm'
import { LedgerList } from '../components/wallet/LedgerList'
import { EmptyState, Spinner } from '../components/ui'

/**
 * Where we are with a top-up whose money hasn't been confirmed on screen yet.
 * `polling` waits on GET /wallet/top_ups/:id; only a `credited` response moves
 * us to `credited` (build-contract §3.7 — the ledger is the ONLY money-arrived
 * signal). `pending` = we stopped polling while Stripe was still settling.
 */
type TopUpPhase = 'idle' | 'polling' | 'credited' | 'pending' | 'failed' | 'error'

/** Stripe redirect_status values that mean "keep waiting", not "it failed". */
function pollable(redirectStatus: string | null): boolean {
  return (
    redirectStatus === null || redirectStatus === 'succeeded' || redirectStatus === 'processing'
  )
}

/**
 * Personal wallet (build-contract §4 `/wallet`): server-truth balance, the
 * signed ledger, and the Payment Element top-up flow. This screen is also the
 * 3DS `return_url` landing — it parses `?top_up_id&redirect_status`, strips
 * them from the URL, and polls the top-up until the ledger credit is written.
 */
export function WalletScreen() {
  usePageTitle('Wallet', LEGACY_PRODUCT_NAME)
  const { features } = useAuth()
  const walletEnabled = features.group_wallet
  const {
    wallet,
    entries,
    entriesHasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMoreEntries,
    createTopUp,
    awaitTopUpCredited,
  } = useWallet({ enabled: walletEnabled })
  const navigate = useNavigate()

  // 3DS return params, captured once at first render (lazy initializer — the
  // effect below strips them from the URL, so re-renders must not re-parse).
  const [topUpReturn] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const topUpId = params.get('top_up_id')
    return topUpId ? { topUpId, redirectStatus: params.get('redirect_status') } : null
  })
  const [phase, setPhase] = useState<TopUpPhase>(() => {
    if (!topUpReturn) return 'idle'
    return pollable(topUpReturn.redirectStatus) ? 'polling' : 'failed'
  })

  const [dialogOpen, setDialogOpen] = useState(false)
  // The top-up created for the currently open dialog — rides the return_url so
  // the 3DS landing knows which top-up to poll.
  const [activeTopUpId, setActiveTopUpId] = useState<string | null>(null)
  const [publishableKey] = useState(() => getStripePublishableKey())

  /** Polls the top-up to a phase; state only changes in promise callbacks. */
  const trackTopUp = useCallback(
    (topUpId: string) =>
      awaitTopUpCredited(topUpId).then(
        (outcome) => {
          if (outcome === 'credited') setPhase('credited')
          else if (outcome === 'pending') setPhase('pending')
          else setPhase('failed')
        },
        () => setPhase('error'),
      ),
    [awaitTopUpCredited],
  )

  // Handle the 3DS return exactly once: strip the params (so refresh/back
  // doesn't replay it), then start polling when the redirect wasn't a failure.
  const returnHandled = useRef(false)
  useEffect(() => {
    if (!topUpReturn || returnHandled.current) return
    returnHandled.current = true
    void navigate({ to: '/steam/wallet', search: {}, replace: true })
    if (walletEnabled && phase === 'polling') {
      void trackTopUp(topUpReturn.topUpId)
    }
  }, [navigate, phase, topUpReturn, trackTopUp, walletEnabled])

  const handleCreateIntent = useCallback(
    async (amountCents: number) => {
      const { topUpId, clientSecret } = await createTopUp(amountCents)
      setActiveTopUpId(topUpId)
      return { clientSecret }
    },
    [createTopUp],
  )

  if (!walletEnabled) {
    return (
      <EmptyState
        icon={<WalletIcon className="h-10 w-10" strokeWidth={1.5} />}
        title="The wallet isn’t available here"
        description="Top-ups and group gifting aren’t enabled on this server yet."
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!wallet) {
    return (
      <p role="alert" className="text-sm text-danger-fg">
        {error ?? 'Could not load your wallet.'}
      </p>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="font-display text-3xl leading-tight text-text-strong">Your wallet</h1>
        <p className="mt-1 text-sm text-text-muted">
          Top up here, chip in on group gifts anywhere.
        </p>
      </header>

      {phase === 'polling' && (
        <p role="status" className="flex items-center gap-2 text-sm text-text-muted">
          <Spinner size="sm" aria-hidden="true" /> Finishing your top-up…
        </p>
      )}
      {phase === 'credited' && (
        <p role="status" className="text-sm text-success-fg">
          Top-up added to your wallet.
        </p>
      )}
      {phase === 'pending' && (
        <p role="status" className="text-sm text-warning-fg">
          Your top-up is still settling — it can take a minute to show up here.
        </p>
      )}
      {phase === 'failed' && (
        <p role="alert" className="text-sm text-danger-fg">
          That top-up didn’t go through, so nothing was added to your wallet.
        </p>
      )}
      {phase === 'error' && (
        <p role="alert" className="text-sm text-danger-fg">
          We couldn’t confirm your top-up — reload to check your balance.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger-fg">
          {error}
        </p>
      )}

      <WalletBalanceCard
        wallet={wallet}
        publishableKey={publishableKey}
        onAddFunds={() => setDialogOpen(true)}
      />

      <section className="space-y-3">
        <h2 className="font-display text-lg text-text-strong">History</h2>
        <LedgerList
          entries={entries}
          hasMore={entriesHasMore}
          onLoadMore={() => void loadMoreEntries()}
          loadingMore={isLoadingMore}
        />
      </section>

      <TopUpDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreateIntent={handleCreateIntent}
      >
        {({ clientSecret, onBack }) => (
          <TopUpPaymentForm
            clientSecret={clientSecret}
            returnUrl={walletReturnUrl(activeTopUpId ?? '')}
            onConfirmed={() => {
              // In-place confirmation only advances the UI — the money arrives
              // when the ledger credit lands, so poll for `credited`.
              setDialogOpen(false)
              setPhase('polling')
              if (activeTopUpId) void trackTopUp(activeTopUpId)
            }}
            onCancel={onBack}
          />
        )}
      </TopUpDialog>
    </div>
  )
}
